import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
} from "@mui/material";
import {
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  NoteAdd as InspectionIcon,
  SwapHoriz as MoveIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

interface ApiaryContextMenuProps {
  apiaryId: number;
  apiaryName: string;
  onDelete?: () => void;
  onMoveHives?: () => void;
  isOwner?: boolean;
  disabled?: boolean;
}

const ApiaryContextMenu: React.FC<ApiaryContextMenuProps> = ({
  apiaryId,
  apiaryName,
  onDelete,
  onMoveHives,
  isOwner = false,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleEditApiary = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/apiaries/${apiaryId}/edit`);
    handleClose();
  };

  const handleNewInspection = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/inspections/create`, { state: { apiaryId } });
    handleClose();
  };

  const handleViewInspections = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/inspections`, { state: { apiaryId, filter: true } });
    handleClose();
  };

  const handleNewHive = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate("/hives/create", { state: { apiaryId } });
    handleClose();
  };

  const handleMoveHives = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onMoveHives) {
      onMoveHives();
    }
    handleClose();
  };

  const handleDeleteApiary = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (
      window.confirm(
        `Are you sure you want to delete apiary "${apiaryName}"? This will also remove all hives in this apiary.`,
      )
    ) {
      if (onDelete) {
        onDelete();
      }
    }
    handleClose();
  };

  if (!isOwner && !onDelete) {
    return null;
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        disabled={disabled}
        sx={{
          color: "text.secondary",
          "&:hover": {
            color: "text.primary",
            backgroundColor: "action.hover",
          },
        }}
        aria-label="apiary options"
        aria-controls={open ? "apiary-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <MoreIcon />
      </IconButton>

      <Menu
        id="apiary-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={(event: any) => handleClose(event)}
        onClick={(e) => e.stopPropagation()}
        MenuListProps={{
          "aria-labelledby": "apiary-options-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            boxShadow: 2,
            borderRadius: 1,
            minWidth: 200,
          },
        }}
      >
        <MenuItem onClick={handleEditApiary}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Apiary</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleNewInspection}>
          <ListItemIcon>
            <InspectionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New Inspection</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleViewInspections}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Inspections</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleNewHive}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New Hive</ListItemText>
        </MenuItem>

        {isOwner && onMoveHives && (
          <MenuItem onClick={handleMoveHives}>
            <ListItemIcon>
              <MoveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Move Hives</ListItemText>
          </MenuItem>
        )}

        {isOwner && onDelete && (
          <>
            <Divider />
            <MenuItem
              onClick={handleDeleteApiary}
              sx={{
                color: "error.main",
                "&:hover": {
                  backgroundColor: "error.light",
                  color: "error.dark",
                },
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Remove Apiary</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export default ApiaryContextMenu;
